import { useCallback, useEffect, useRef, useState } from "react";
import { StickyNote as StickyIcon, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useStickyNotes, type StickyColor, type StickyNote } from "@/hooks/useStickyNotes";
import { StickyNoteCard, STICKY_HEIGHT, STICKY_WIDTH } from "./StickyNoteCard";

interface Props {
  leadId: string;
  /** Stable key for the part of the build these notes belong to, e.g. "itinerary". */
  section: string;
  /** Shown next to the add button so people know where the note lives. */
  label?: string;
  readOnly?: boolean;
}

interface DragState {
  id: string;
  offsetX: number;
  offsetY: number;
  x: number;
  y: number;
}

export function StickyNoteBoard({ leadId, section, label = "Sticky notes", readOnly }: Props) {
  const { notes, create, updateContent, updatePosition, updateColor, remove } = useStickyNotes(
    leadId,
    section
  );
  const boardRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<DragState | null>(null);

  const clamp = useCallback((x: number, y: number) => {
    const width = boardRef.current?.clientWidth ?? STICKY_WIDTH;
    const maxX = Math.max(0, width - STICKY_WIDTH);
    return { x: Math.min(Math.max(0, x), maxX), y: Math.max(0, y) };
  }, []);

  const handleDragStart = (e: React.PointerEvent, note: StickyNote) => {
    if (readOnly) return;
    const board = boardRef.current;
    if (!board) return;
    e.preventDefault();
    const rect = board.getBoundingClientRect();
    setDrag({
      id: note.id,
      offsetX: e.clientX - rect.left - note.sticky_x,
      offsetY: e.clientY - rect.top - note.sticky_y,
      x: note.sticky_x,
      y: note.sticky_y,
    });
  };

  useEffect(() => {
    if (!drag) return;

    const move = (e: PointerEvent) => {
      const board = boardRef.current;
      if (!board) return;
      const rect = board.getBoundingClientRect();
      const next = clamp(e.clientX - rect.left - drag.offsetX, e.clientY - rect.top - drag.offsetY);
      setDrag((prev) => (prev ? { ...prev, ...next } : prev));
    };

    const end = () => {
      setDrag((prev) => {
        if (prev) {
          updatePosition.mutate(
            { id: prev.id, x: prev.x, y: prev.y },
            { onError: (err: any) => toast.error("Could not move the note", { description: err.message }) }
          );
        }
        return null;
      });
    };

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag?.id, drag?.offsetX, drag?.offsetY, clamp]);

  // Keep notes inside the board when it gets narrower
  useEffect(() => {
    const onResize = () => {
      const width = boardRef.current?.clientWidth;
      if (!width) return;
      const maxX = Math.max(0, width - STICKY_WIDTH);
      notes
        .filter((n) => n.sticky_x > maxX)
        .forEach((n) => updatePosition.mutate({ id: n.id, x: maxX, y: n.sticky_y }));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes]);

  const addNote = () => {
    const color: StickyColor = "yellow";
    const offset = (notes.length % 4) * 24;
    create.mutate(
      { x: 8 + offset, y: 8 + offset, color },
      {
        onError: (err: any) => toast.error("Could not add note", { description: err.message }),
        onSuccess: () => toast.success("Sticky note added"),
      }
    );
  };

  const boardHeight = notes.length
    ? Math.max(...notes.map((n) => (drag?.id === n.id ? drag.y : n.sticky_y) + STICKY_HEIGHT)) + 16
    : 0;

  if (readOnly && notes.length === 0) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
          <StickyIcon className="h-3.5 w-3.5" />
          {label}
          {notes.length > 0 && <span className="opacity-70">({notes.length})</span>}
        </p>
        {!readOnly && (
          <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={addNote} disabled={create.isPending}>
            <Plus className="h-3.5 w-3.5" /> Sticky note
          </Button>
        )}
      </div>

      {notes.length > 0 && (
        <div
          ref={boardRef}
          className="relative rounded-md border border-dashed bg-muted/30 overflow-x-auto overflow-y-hidden"
          style={{ height: boardHeight, touchAction: drag ? "none" : undefined }}
        >
          {notes.map((note) => (
            <StickyNoteCard
              key={note.id}
              note={drag?.id === note.id ? { ...note, sticky_x: drag.x, sticky_y: drag.y } : note}
              readOnly={readOnly}
              isDragging={drag?.id === note.id}
              onDragStart={handleDragStart}
              onSave={(title, content) =>
                updateContent.mutate(
                  { id: note.id, title, content },
                  { onError: (err: any) => toast.error("Could not save note", { description: err.message }) }
                )
              }
              onColor={(color) => updateColor.mutate({ id: note.id, color })}
              onDelete={() => remove.mutate(note.id)}
            />
          ))}
        </div>
      )}
      {!notes.length && !readOnly && (
        <div ref={boardRef} className="hidden" />
      )}
    </div>
  );
}
